/**
 * @file index.tsx
 *
 * @fileoverview Renders landing page facet categories. Displays the categories as a grid on desktop
 * and as an accordion on mobile.
 */
import { FacetCategoriesParagraph } from '~/jobiqo/types/types';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Box } from '~/vcl/atoms/07-box';
import FacetCategory from './components/FacetCategoryBlock';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { HideDesktop } from '~/vcl/atoms/06-layout/02-visibility/01-hide-desktop';
import { PersistentAccordion } from '~/vcl/atoms/25-accordion/01-persistent-accordion';
import { getFacetLinks } from './utils/getFacetLinks';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';

interface FacetCategoriesProps {
  /**
   * The job teasers row.
   */
  row: FacetCategoriesParagraph;
  /**
   * The size for the container.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export default function FacetCategories({
  row,
  maxContainerSize,
}: FacetCategoriesProps) {
  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      marginTop={0}
      paddingTop={row.spacing ?? 11}
      paddingBottom={row.spacing ?? 11}
      {...(row.background && {
        backgroundColorOverride: `var(--color-${row.background})`,
      })}
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        {row.facetCategoriesSubTitle && (
          <>
            <Text
              textAlign="center"
              color={row.color as any}
              fontWeight="bold"
              textTransform="uppercase"
            >
              {row.facetCategoriesSubTitle}
            </Text>
            <Spacing size="medium" />
          </>
        )}
        {row.facetCategoriesTitle && (
          <>
            <Text
              textAlign="center"
              tag="h2"
              fontSize={[5, 6]}
              lineHeight={[5, 6]}
            >
              {row.facetCategoriesTitle}
            </Text>
            <Spacing size="large" />
          </>
        )}
        {row.text && (
          <>
            <Text textAlign="center" color="gray6" fontSize={4} lineHeight={5}>
              {row.text}
            </Text>
            <Spacing marginBottom={11} />
          </>
        )}
        <HideMobile>
          <Grid
            columns={'repeat(auto-fit, minmax(200px, 1fr))'}
            justifyItems={['left', 'center']}
          >
            {row.categories?.map(
              (category, index) =>
                category && <FacetCategory key={index} category={category} />,
            )}
          </Grid>
        </HideMobile>
        <HideDesktop>
          <PersistentAccordion
            collapsible={true}
            items={
              row.categories?.map((category, index) => ({
                content: (
                  <ul>
                    {getFacetLinks(category, ({ children }) => (
                      <li>{children}</li>
                    ))}
                  </ul>
                ),
                title: category?.title,
                value: `item-${index}`,
              })) ?? []
            }
          />
        </HideDesktop>
      </Main>
    </Box>
  );
}
export { FacetCategories };
